import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Badge,
  Avatar,
  Tooltip,
  Modal,
  Popover,
  Descriptions,
  Statistic,
  Row,
  Col,
  DatePicker,
  message,
  Dropdown,
  Menu,
  Layout,
  Tabs,
  Form,
  Upload,
  Empty,
  InputNumber,
  Grid,
  Drawer,
  Checkbox,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  BankOutlined,
  IdcardOutlined,
  UploadOutlined,
  CalendarOutlined,
  CrownOutlined,
  DownloadOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  ShopOutlined,
  ScanOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SoundOutlined,
  WhatsAppOutlined,
  ContactsOutlined,
  MessageOutlined,
  PlusOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import moment from "moment";
import SpeakerManagement from "./SpeakerManagement";
import ExhibitorManagement from "./ExhibitorManagement";
import WhatsAppStatusCell from "./WhatsAppStatusCell";
import NextStepsChecklist from "./NextStepsChecklist";
import RegistrationCardList from "./RegistrationCardList";
import RegistrationsEmptyState from "./RegistrationsEmptyState";
import PhoneField from "../shared/PhoneField";
import ImportGuestsModal from "./ImportGuestsModal";
import SendInvitationsModal from "./SendInvitationsModal";
import ThankYouMessagePreview from "./ThankYouMessagePreview";
import { showFeatureNotIncludedModal } from "../../utils/featureGate";
import { formatDate } from "../shared/DateField";
import { digitsFormatter, digitsParser, toArabicDigits } from "../../utils/digits";
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Content } = Layout;

// Extra choices in the status filter that are not real statuses; the server treats them as special lists.
const GUEST_FILTERS = ["changed", "not_sent", "sent", "delivered", "failed", "no_phone", "no_email"];
// Mirrors the server's WhatsAppText.DefaultThankYouText and EventFormPage's own copy of the same default.
const DEFAULT_THANK_YOU_TEXT = "نشكركم على تشريفنا وحضوركم";

// One clear step per guest: Not sent -> Sent -> Delivered, or Failed. An answer always wins.
// (WhatsApp "read" is not reliable - guests can turn read receipts off - so it counts as Delivered.)
const guestStage = (record) => {
  if (record.status === "confirmed" || record.status === "checked_in") return "confirmed";
  if (record.status === "cancelled") return "declined";
  const d = record.whatsAppDeliveryStatus;
  if (d === "failed" || d === "rejected") return "failed";
  if (d === "delivered" || d === "read") return "delivered";
  if (d || record.whatsAppMessageId) return "sent";
  return "not_sent";
};

const EventRegistrationsPage = () => {
  const { t, i18n } = useTranslation();
  const { profile, primaryColor, isVirtual } = useContext(AppContext);
  const navigate = useNavigate();
  const { id } = useParams();
  // `=== false` so the first render (before antd reports breakpoints) defaults to desktop.
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const isDesktop = !!screens.lg;
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [eventNotFound, setEventNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [allTabPagination, setAllTabPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [addVipModalVisible, setAddVipModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [sendInvitesVisible, setSendInvitesVisible] = useState(false);
  const [addVipLoading, setAddVipLoading] = useState(false);
  const [addVipForm] = Form.useForm();
  // Which registration type the shared Add-Participant modal creates. Defaults to "vip" so the
  // existing "Add VIP Participant" button (Normal Events) keeps its exact prior behavior; the
  // Regular Guests tab's "Add Guest"/"Add Regular Guest" button sets this to "regular" before
  // opening the same modal. Editing an existing participant ignores this and keeps its own type.
  const [guestTypeToAdd, setGuestTypeToAdd] = useState("vip");

  // Check-in history modal state
  const [hasWhatsAppSupport, setHasWhatsAppSupport] = useState(false);
  const [hasDigitalPassSupport, setHasDigitalPassSupport] = useState(false);
  const [hasExportSupport, setHasExportSupport] = useState(false);
  // Real invitation limit from the event's backing entitlement (Wedding events only), used to
  // show a count/limit indicator instead of an unqualified running total.
  const [maxInvitations, setMaxInvitations] = useState(null);
  const [bulkWhatsAppLoading, setBulkWhatsAppLoading] = useState(false);
  const [thankYouLoading, setThankYouLoading] = useState(false);
  const [thankYouEditOpen, setThankYouEditOpen] = useState(false);
  const [thankYouTextDraft, setThankYouTextDraft] = useState(DEFAULT_THANK_YOU_TEXT);
  const [thankYouTextSaving, setThankYouTextSaving] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRegistration, setHistoryRegistration] = useState(null);
  // Check-in state for speakers/exhibitors, keyed by entity id (the event object doesn't carry it)
  const [entityCheckIns, setEntityCheckIns] = useState({});

  // Weddings use EventType "marriage" (not the commercial ProductType "wedding" the package/
  // entitlement is sold under). Wedding events have no VIP concept — only regular guests.
  const isMarriageEvent = event?.eventType === "marriage";

  // True while the event has nothing at all (no guests, speakers or exhibitors). Drives the guided
  // empty state and hides the stats/filters, which are noise until there is data. Treated as true
  // until the event loads so the empty state never flashes in.
  const hasAnyData =
    !event ||
    (event.totalRegistrations || 0) > 0 ||
    (!isMarriageEvent && ((event.speakers?.length || 0) > 0 || (event.exhibitors?.length || 0) > 0));

  // The opening tab is chosen once per page visit, after the event loads.
  const initialTabChosen = useRef(false);

  useEffect(() => {
    if (id) {
      loadEventData();
    }
  }, [id]);

  const loadEntityCheckIns = async () => {
    const entities = [...(event?.speakers || []), ...(event?.exhibitors || [])];
    if (!id || entities.length === 0) return;
    const results = await Promise.all(
      entities.map(async (entity) => {
        try {
          const { data } = await axios.get(
            `${BASE_URL}/CheckIn/event/${id}/attendee/${entity.id}/time-report`
          );
          return [entity.id, data];
        } catch (error) {
          return [entity.id, null];
        }
      })
    );
    setEntityCheckIns(Object.fromEntries(results.filter(([, data]) => data)));
  };

  useEffect(() => {
    loadEntityCheckIns();
  }, [event?.speakers, event?.exhibitors]);

  // Handle activeTab changes - reload registrations with appropriate type filter
  // Waits for the event so weddings (single "Guests" tab, no "all" tab) don't first fetch every row.
  const eventLoaded = !!event;
  useEffect(() => {
    if (!id || !eventLoaded) return;
    let tab = isMarriageEvent && activeTab === "all" ? "regular" : activeTab;
    if (!initialTabChosen.current) {
      initialTabChosen.current = true;
      // An empty general event opens on Regular Guests, where the "Add Guest" action lives.
      if (tab === "all" && !hasAnyData) tab = "regular";
    }
    if (tab !== activeTab) {
      setActiveTab(tab);
      return;
    }
    if (tab !== "all") {
      // When switching to regular or vip tab, reload with that specific type filter
      loadRegistrations(1, 10, searchText, statusFilter, tab);
    } else {
      // When switching to all tab, load ALL registrations (no pagination) to combine with speakers/exhibitors
      setAllTabPagination((prev) => ({ ...prev, current: 1 }));
      loadRegistrations(1, 10000, searchText, statusFilter, "all");
    }
  }, [activeTab, id, eventLoaded, isMarriageEvent]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    // Debounce search
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      if (activeTab === "all") {
        setAllTabPagination((prev) => ({ ...prev, current: 1 }));
        loadRegistrations(1, 10000, value, statusFilter, "all");
      } else {
        loadRegistrations(1, pagination.pageSize, value, statusFilter, typeFilter);
      }
    }, 500);
    setSearchTimeout(timeout);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (activeTab === "all") {
      setAllTabPagination((prev) => ({ ...prev, current: 1 }));
      loadRegistrations(1, 10000, searchText, value, "all");
    } else {
      loadRegistrations(1, pagination.pageSize, searchText, value, typeFilter);
    }
  };

  const handleTypeChange = (value) => {
    setTypeFilter(value);
    loadRegistrations(1, pagination.pageSize, searchText, statusFilter, value);
  };

  const handleDateChange = (dates) => {
    setDateFilter(dates);
    // Assuming loadRegistrations handles dates or you update it
    if (activeTab === "all") {
      setAllTabPagination((prev) => ({ ...prev, current: 1 }));
      loadRegistrations(1, 10000, searchText, statusFilter, "all");
    } else {
      loadRegistrations(1, pagination.pageSize, searchText, statusFilter, typeFilter);
    }
  };

  const loadEventData = async () => {
    if (!id) return;

    try {
      // const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BASE_URL}/Event/${id}?companyId=${profile.companyId}`
      );

      setEvent(response.data);
      setEventNotFound(false);

      // Legacy/non-virtual companies predate the entitlement system and are grandfathered
      // into WhatsApp as part of their existing HasEventAccess/MaxEventsCount flow (mirrors
      // the backend's EventHasWhatsAppEntitlementAsync) - no entitlement lookup applies to them.
      // Entitlement is resolved authoritatively on the backend for every send path regardless;
      // this is only used to disable the button in the UI ahead of time for virtual companies.
      // Derived locally (not from the `event` state, which hasn't committed yet this render).
      const isWeddingEvent = response.data?.eventType === "marriage";
      const eventCapacity = response.data?.capacity > 0 ? response.data.capacity : null;
      if (!isVirtual) {
        setHasWhatsAppSupport(true);
        setHasDigitalPassSupport(true);
        setHasExportSupport(true);
        // Legacy Weddings have no entitlement to match by ID - the event's own Capacity
        // (set at creation, see EventFormPage.jsx) is the real invitation limit for them.
        setMaxInvitations(isWeddingEvent ? eventCapacity : null);
      } else {
        try {
          const entitlementsRes = await axios.get(`${BASE_URL}/Company/my-entitlements`);
          const entitlements = entitlementsRes.data || [];
          const eventEntitlement = response.data?.entitlementId
            ? entitlements.find((e) => e.id === response.data.entitlementId)
            : null;
          const relevantEntitlement = eventEntitlement || entitlements.find((e) => e.status === "active");
          setHasWhatsAppSupport(!!relevantEntitlement?.hasWhatsAppSupport);
          setHasDigitalPassSupport(!!relevantEntitlement?.hasDigitalPassSupport);
          setHasExportSupport(!!relevantEntitlement?.hasExportSupport);
          if (isWeddingEvent) {
            // Wedding invitation limit must come strictly from THIS event's own matching
            // entitlement (by entitlementId) - never an arbitrary other active entitlement,
            // which could belong to a completely different Wedding package. Fall back to the
            // event's own Capacity only, mirroring the same fallback the backend's own
            // registration capacity check uses (see EventRegistrationService.cs).
            setMaxInvitations(
              eventEntitlement?.maxInvitations > 0 ? eventEntitlement.maxInvitations : eventCapacity
            );
          } else {
            setMaxInvitations(relevantEntitlement?.maxInvitations > 0 ? relevantEntitlement.maxInvitations : null);
          }
        } catch (entitlementError) {
          console.error("Error fetching entitlements:", entitlementError);
          setHasWhatsAppSupport(false);
          setHasDigitalPassSupport(false);
          setHasExportSupport(false);
          setMaxInvitations(isWeddingEvent ? eventCapacity : null);
        }
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      if (error.response?.status === 404) {
        setEventNotFound(true);
        message.error(t("eventRegistrations.errors.eventNotFound"));
      } else {
        message.error(
          t("eventRegistrations.errors.loadEventFailed") +
          (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const loadRegistrations = async (
    page = 1,
    pageSize = 10,
    search = searchText,
    status = statusFilter,
    type = typeFilter,
    dates = dateFilter
  ) => {
    if (!id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        take: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        ...(search && { searchText: search }),
        ...(status !== "all" && !GUEST_FILTERS.includes(status) && { status }),
        ...(GUEST_FILTERS.includes(status) && { guestFilter: status }),
        ...(type !== "all" && { registrationType: type }),
        ...(dates && dates[0] && { startDate: dates[0].format("YYYY-MM-DD") }),
        ...(dates && dates[1] && { endDate: dates[1].format("YYYY-MM-DD") }),
        companyId: profile.companyId,
      });

      const response = await axios.get(
        `${BASE_URL}/EventRegistration/event/${id}?${params}`,
      );

      const { items, totalCount } = response.data;

      // Transform API data to match the expected format
      const transformedRegistrations = items.map((reg) => ({
        id: reg.id,
        name: `${reg.firstName} ${reg.lastName}`.trim(),
        firstName: reg.firstName,
        lastName: reg.lastName,
        email: reg.email,
        phone: reg.phoneNumber || reg.phone || "N/A",
        company: reg.company || "N/A",
        position: reg.jobTitle || reg.position || "N/A",
        type: reg.registrationType || "regular",
        vipPackages: reg.vipPackages || [],
        status: reg.status || "pending",
        registrationDate: reg.registrationDate || reg.createdAt,
        paymentStatus: reg.paymentStatus || "pending",
        amount: reg.amount || 0,
        notes: reg.notes || "",
        companions: reg.companions ?? 0,
        responseChangeCount: reg.responseChangeCount ?? 0,
        responseChangedAt: reg.responseChangedAt || null,
        cancellationReason: reg.cancellationReason || null,
        whatsAppDeliveryStatus: reg.whatsAppDeliveryStatus || null,
        whatsAppMessageId: reg.whatsAppMessageId || null,
        whatsAppStatusUpdatedAt: reg.whatsAppStatusUpdatedAt || null,
        whatsAppErrorCode: reg.whatsAppErrorCode ?? null,
        whatsAppErrorMessage: reg.whatsAppErrorMessage || null,
        whatsAppStatusHistory: reg.whatsAppStatusHistory || [],
        avatar: reg.avatar || null,
        checkedIn: reg.checkedIn || false,
        checkedInAt: reg.checkedInAt || null,
        checkedInGate: reg.checkedInGate || null,
        checkedInBy: reg.checkedInBy || null,
        checkedOut: reg.checkedOut || false,
        checkedOutAt: reg.checkedOutAt || null,
        checkedOutGate: reg.checkedOutGate || null,
        checkedOutBy: reg.checkedOutBy || null,
        lastActionType: reg.lastActionType || null,
        currentCycleNumber: reg.currentCycleNumber || null,
        totalDurationInside: reg.totalDurationInside || null,
        totalDurationInsideFormatted: reg.totalDurationInsideFormatted || null,
        firstCheckInTime: reg.firstCheckInTime || null,
        lastCheckOutTime: reg.lastCheckOutTime || null,
        journeyLog: reg.journeyLog || null,
      }));

      setRegistrations(transformedRegistrations);
      setFilteredRegistrations(transformedRegistrations);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: totalCount,
      });
    } catch (error) {
      console.error("Error fetching registrations:", error);
      message.error(
        t("eventRegistrations.errors.loadRegistrationsFailed") +
        (error.response?.data?.message || error.message)
      );
      setRegistrations([]);
      setFilteredRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
    setDetailModalVisible(true);
  };

  const handleExport = async () => {
    if (!id) return;
    if (!hasExportSupport) {
      showFeatureNotIncludedModal(
        t("eventRegistrations.messages.exportNotIncluded"),
        t("common.featureNotIncludedTitle"),
        t("common.ok")
      );
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}/CheckIn/event/${id}/export`, {
        responseType: "blob",
      });
      downloadExportBlob(response, `attendees-${id}.csv`);
    } catch (error) {
      console.error("Error exporting registrations:", error);
      if (error.response?.status === 400) {
        message.error(t("eventRegistrations.messages.exportNotIncluded"));
      } else {
        message.error(t("eventRegistrations.errors.exportFailed"));
      }
    }
  };

  // Shared by the full-event export and the bulk-selection export (which currently exports the
  // same full file - see handleBulkAction's "export" case for why).
  const downloadExportBlob = (response, fallbackFilename) => {
    const disposition = response.headers?.["content-disposition"];
    const match = disposition && /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match ? match[1] : fallbackFilename;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // const handleSendEmail = (registration) => {
  //   // Mock email functionality
  //   message.success(`Email sent to ${registration.name}`);
  // };

  // Shows a small "not included in your plan" popup instead of running the action, when the
  // relevant entitlement flag is off. Used in place of disabled+title, which gives no feedback
  // on touch devices (disabled buttons don't fire taps, native title tooltips don't show).
  const requireFeature = (included, messageKey, action) => {
    if (!included) {
      showFeatureNotIncludedModal(t(messageKey), t("common.featureNotIncludedTitle"), t("common.ok"));
      return;
    }
    action();
  };

  const handleSendCard = async (registration) => {
    try {
      message.loading({ content: t("eventRegistrations.messages.sendingCard"), key: 'sendCard' });

      const response = await axios.post(`${BASE_URL}/EventWallet/send-card`, {
        registrationId: registration.id
      });

      if (response.data.success) {
        message.success({
          content: t("eventRegistrations.messages.cardSent", { email: registration.email }),
          key: 'sendCard',
          duration: 4
        });
      } else {
        message.error({
          content: response.data.message || t("eventRegistrations.errors.sendCardFailed"),
          key: 'sendCard'
        });
      }
    } catch (error) {
      console.error('Error sending card:', error);
      message.error({
        content: error.response?.data?.message || t("eventRegistrations.errors.sendCardEmailFailed"),
        key: 'sendCard'
      });
    }
  };

  const handleSendWhatsApp = async (registration) => {
    try {
      message.loading({ content: t("eventRegistrations.messages.sendingWhatsapp"), key: "sendWhatsApp" });

      const response = await axios.post(`${BASE_URL}/EventWallet/send-whatsapp`, {
        registrationId: registration.id,
        invitedBy: event?.invitedBy,
      });

      if (response.data.success) {
        message.success({
          content: t("eventRegistrations.messages.whatsappSent"),
          key: "sendWhatsApp",
          duration: 4,
        });
        // Reload so the guest shows as Sent and cannot be sent the same invitation again.
        handleRefresh();
      } else {
        message.error({
          content: response.data.message || t("eventRegistrations.errors.sendWhatsappFailed"),
          key: "sendWhatsApp",
        });
      }
    } catch (error) {
      console.error("Error sending WhatsApp invite:", error);
      message.error({
        content: error.response?.data?.message || t("eventRegistrations.errors.sendWhatsappFailed"),
        key: "sendWhatsApp",
      });
    }
  };

  // Fetch full check-in/checkout history for an attendee
  const handleViewHistory = async (registration) => {
    setHistoryRegistration(registration);
    setHistoryModalVisible(true);
    setHistoryLoading(true);
    setHistoryData(null);

    try {
      const response = await axios.get(
        `${BASE_URL}/CheckIn/event/${id}/attendee/${registration.id}/time-report`
      );
      setHistoryData(response.data);
    } catch (error) {
      console.error("Error fetching check-in history:", error);
      message.error(t("eventRegistrations.errors.loadHistoryFailed"));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteParticipant = (registration) => {
    Modal.confirm({
      title: t("eventRegistrations.confirmRemove.title"),
      content: t("eventRegistrations.confirmRemove.content", { name: registration.name }),
      okText: t("eventRegistrations.confirmRemove.okText"),
      okType: "danger",
      cancelText: t("eventRegistrations.confirmRemove.cancelText"),
      onOk: async () => {
        try {
          message.loading({ content: t("eventRegistrations.messages.removingParticipant"), key: "deleteParticipant" });

          const response = await axios.delete(
            `${BASE_URL}/EventRegistration/${registration.id}?companyId=${profile.companyId}&eventId=${id}`
          );

          message.success({
            content: t("eventRegistrations.messages.participantRemoved", { name: registration.name }),
            key: "deleteParticipant",
            duration: 3
          });

          // Refresh the registrations list
          handleRefresh();
        } catch (error) {
          console.error("Error deleting participant:", error);
          message.error({
            content: error.response?.data?.message || t("eventRegistrations.errors.removeParticipantFailed"),
            key: "deleteParticipant"
          });
        }
      },
    });
  };

  const handleBulkAction = async (action) => {
    const selectedRegs = registrations.filter((reg) =>
      selectedRowKeys.includes(reg.id)
    );
    switch (action) {
      case "confirm":
        message.success(t("eventRegistrations.messages.bulkConfirmed", { count: selectedRegs.length }));
        break;
      case "email":
        message.success(t("eventRegistrations.messages.bulkEmailSent", { count: selectedRegs.length }));
        break;
      case "export":
        if (!hasExportSupport) {
          showFeatureNotIncludedModal(
            t("eventRegistrations.messages.exportNotIncluded"),
            t("common.featureNotIncludedTitle"),
            t("common.ok")
          );
          break;
        }
        // The backend export endpoint has no way to filter by specific registration ids, so a
        // bulk/selection export currently downloads the same full-event file as the toolbar export.
        message.loading({
          content: t("eventRegistrations.messages.bulkExporting", { count: selectedRegs.length }),
          key: "bulkExport",
        });
        try {
          const response = await axios.get(`${BASE_URL}/CheckIn/event/${id}/export`, {
            responseType: "blob",
          });
          downloadExportBlob(response, `attendees-${id}.csv`);
          message.destroy("bulkExport");
        } catch (error) {
          console.error("Error exporting registrations:", error);
          if (error.response?.status === 400) {
            message.error({ content: t("eventRegistrations.messages.exportNotIncluded"), key: "bulkExport" });
          } else {
            message.error({ content: t("eventRegistrations.errors.exportFailed"), key: "bulkExport" });
          }
        }
        break;
      case "whatsapp": {
        setBulkWhatsAppLoading(true);
        message.loading({
          content: t("eventRegistrations.messages.sendingBulkWhatsapp", { count: selectedRegs.length }),
          key: "bulkWhatsApp",
          duration: 0,
        });
        try {
          const response = await axios.post(`${BASE_URL}/EventWallet/send-whatsapp/bulk`, {
            eventId: id,
            registrationIds: selectedRowKeys,
            invitedBy: event?.invitedBy,
          });
          const {
            successCount = 0,
            failedCount = 0,
            totalCount = selectedRegs.length,
          } = response.data || {};
          if (failedCount > 0) {
            message.warning({
              content: t("eventRegistrations.messages.bulkWhatsappPartial", {
                success: successCount,
                total: totalCount,
                failed: failedCount,
              }),
              key: "bulkWhatsApp",
              duration: 5,
            });
          } else {
            message.success({
              content: t("eventRegistrations.messages.bulkWhatsappSent", { count: successCount }),
              key: "bulkWhatsApp",
              duration: 4,
            });
          }
        } catch (error) {
          console.error("Error sending bulk WhatsApp invites:", error);
          message.error({
            content: error.response?.data?.message || t("eventRegistrations.errors.sendBulkWhatsappFailed"),
            key: "bulkWhatsApp",
          });
        } finally {
          setBulkWhatsAppLoading(false);
        }
        break;
      }
      default:
        break;
    }
    setSelectedRowKeys([]);
  };

  // Manual-only: the organiser presses this when ready (not every event wants one, and some are
  // reviewed before sending), unlike invitations/reminders there is no automatic trigger. The server
  // selects who gets it (checked in, has a phone, not already thanked) - this isn't tied to row selection.
  const handleSendThankYou = () => {
    Modal.confirm({
      title: t("eventRegistrations.sendThankYou.confirmTitle"),
      content: t("eventRegistrations.sendThankYou.confirmText"),
      okText: t("eventRegistrations.sendThankYou.send"),
      cancelText: t("eventRegistrations.sendThankYou.cancel"),
      onOk: async () => {
        setThankYouLoading(true);
        message.loading({
          content: t("eventRegistrations.messages.sendingThankYou"),
          key: "thankYou",
          duration: 0,
        });
        try {
          const response = await axios.post(`${BASE_URL}/EventWallet/send-thank-you/bulk`, {
            eventId: id,
          });
          const {
            successCount = 0,
            failedCount = 0,
            totalCount = 0,
          } = response.data || {};
          if (totalCount === 0) {
            message.info({
              content: t("eventRegistrations.messages.thankYouNoneEligible"),
              key: "thankYou",
              duration: 4,
            });
          } else if (failedCount > 0) {
            message.warning({
              content: t("eventRegistrations.messages.thankYouPartial", {
                success: successCount,
                total: totalCount,
                failed: failedCount,
              }),
              key: "thankYou",
              duration: 5,
            });
          } else {
            message.success({
              content: t("eventRegistrations.messages.thankYouSent", { count: successCount }),
              key: "thankYou",
              duration: 4,
            });
          }
        } catch (error) {
          console.error("Error sending thank-you messages:", error);
          message.error({
            content: error.response?.data?.message || t("eventRegistrations.errors.sendThankYouFailed"),
            key: "thankYou",
          });
        } finally {
          setThankYouLoading(false);
        }
      },
    });
  };

  // Guards the send button: there is nothing to thank anyone for until the event is actually over,
  // and guests can't have checked in to an event that hasn't started. endDate/endTime mirror the
  // server's own fields; no end time saved defaults to end-of-day, same convention as EventFormPage.
  const eventHasEnded = (() => {
    if (!event?.endDate) return false;
    const endDateOnly = String(event.endDate).slice(0, 10);
    const endTime = event.endTime ? String(event.endTime).slice(0, 8) : "23:59:59";
    const end = new Date(`${endDateOnly}T${endTime}`);
    return !Number.isNaN(end.getTime()) && end.getTime() <= Date.now();
  })();

  const openThankYouEdit = () => {
    setThankYouTextDraft(event?.thankYouText || DEFAULT_THANK_YOU_TEXT);
    setThankYouEditOpen(true);
  };

  // Saved separately from the full event (PATCH, not the whole EventDto) so this page can let the
  // organiser tweak just this one line without needing a complete, current copy of the event to
  // resubmit - this page doesn't carry one.
  const handleSaveThankYouText = async () => {
    setThankYouTextSaving(true);
    try {
      const response = await axios.patch(`${BASE_URL}/Event/${id}/thank-you-text`, {
        thankYouText: thankYouTextDraft === DEFAULT_THANK_YOU_TEXT ? null : thankYouTextDraft,
      });
      setEvent((prev) => (prev ? { ...prev, thankYouText: response.data?.thankYouText ?? null } : prev));
      message.success(t("eventRegistrations.sendThankYou.textSaved"));
      setThankYouEditOpen(false);
    } catch (error) {
      console.error("Error saving thank-you message:", error);
      message.error(error.response?.data?.message || t("eventRegistrations.sendThankYou.textSaveFailed"));
    } finally {
      setThankYouTextSaving(false);
    }
  };

  // Wedding guests are filtered by journey step (a phone is always required, and email is not used,
  // so the "no phone" / "no email" lists only make sense for general events).
  const statusFilterOptions = isMarriageEvent
    ? [
        { value: "all", label: t("eventRegistrations.filters.allStatus") },
        { value: "not_sent", label: t("eventRegistrations.stage.not_sent") },
        { value: "sent", label: t("eventRegistrations.stage.sent") },
        { value: "delivered", label: t("eventRegistrations.stage.delivered") },
        { value: "failed", label: t("eventRegistrations.stage.failed") },
        { value: "pending", label: t("eventRegistrations.stage.waiting") },
        { value: "confirmed", label: t("eventRegistrations.stage.confirmed") },
        { value: "cancelled", label: t("eventRegistrations.stage.declined") },
        { value: "changed", label: t("eventRegistrations.filters.changed") },
      ]
    : [
        { value: "all", label: t("eventRegistrations.filters.allStatus") },
        { value: "confirmed", label: t("eventRegistrations.filters.confirmed") },
        { value: "pending", label: t("eventRegistrations.filters.pending") },
        { value: "cancelled", label: t("eventRegistrations.filters.cancelled") },
        { value: "changed", label: t("eventRegistrations.filters.changed") },
        { value: "not_sent", label: t("eventRegistrations.filters.not_sent") },
        { value: "no_phone", label: t("eventRegistrations.filters.no_phone") },
        { value: "no_email", label: t("eventRegistrations.filters.no_email") },
      ];

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "green";
      case "pending":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const baseColumns = [
    {
      title: t("eventRegistrations.table.name"),
      dataIndex: "name",
      key: "name",
      width: 250,
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.avatar}
            icon={<UserOutlined />}
            className={record.type === 'vip' ? 'border-2 border-yellow-400' : ''}
          />
          <div>
            <div style={{ fontWeight: "bold" }}>
              {text}
              {record.type === "vip" && (
                <CrownOutlined className="text-yellow-500 ml-1" />
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {record.position}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: t("eventRegistrations.table.contact"),
      key: "contact",
      width: 220,
      render: (_, record) => (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "4px",
            }}
          >
            <MailOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
            {record.email}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <PhoneOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
            {record.phone}
          </div>
        </div>
      ),
    },
    {
      title: t("eventRegistrations.table.company"),
      dataIndex: "company",
      key: "company",
      width: 200,
      ellipsis: true,
    },
    {
      title: t("eventRegistrations.table.type"),
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => {
        let color = "blue";
        let icon = <UserOutlined />;
        let label = t("eventRegistrations.filters.regular");

        if (type === "vip") {
          color = "gold";
          icon = <CrownOutlined />;
          label = t("eventRegistrations.filters.vip");
        } else if (type === "speaker") {
          color = "purple";
          icon = <SoundOutlined />;
          label = t("eventRegistrations.filters.speaker");
        } else if (type === "exhibitor") {
          color = "cyan";
          icon = <ShopOutlined />;
          label = t("eventRegistrations.filters.exhibitor");
        }

        return (
          <Tag color={color} icon={icon}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: t("eventRegistrations.table.status"),
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status, record) => {
        const colors = {
          confirmed: "green",
          pending: "orange",
          cancelled: "red",
        };
        const stage = isMarriageEvent ? guestStage(record) : null;
        const stageColors = {
          not_sent: "default",
          sent: "gold",
          delivered: "blue",
          failed: "red",
          confirmed: "green",
          declined: "red",
        };
        const stageLabel = stage ? t(`eventRegistrations.stage.${stage}`) : null;
        return (
          <Space size={4} direction="vertical" align="start">
            <Space size={4}>
            {stage && ["sent", "delivered", "failed"].includes(stage) ? (
              // Tapping the stage opens the WhatsApp route (queued -> sent -> delivered) for that guest.
              <WhatsAppStatusCell record={record} tagLabel={stageLabel} tagColor={stageColors[stage]} />
            ) : (
              <Tag color={stage ? stageColors[stage] : colors[status]}>
                {stage ? stageLabel : status.toUpperCase()}
              </Tag>
            )}
            {record.responseChangeCount > 0 && (
              <Tooltip
                title={t("eventRegistrations.responseChanged.tooltip", {
                  count: record.responseChangeCount,
                  time: record.responseChangedAt ? moment(record.responseChangedAt).format("MMM DD, HH:mm") : "",
                })}
              >
                <Tag color="purple">{t("eventRegistrations.responseChanged.tag")}</Tag>
              </Tooltip>
            )}
            {status === "cancelled" && (
              <Popover
                title={t("eventRegistrations.table.cancellationReasonTitle")}
                content={
                  <div style={{ maxWidth: 260 }}>
                    {record.cancellationReason ||
                      t("eventRegistrations.table.cancellationReasonMissing")}
                  </div>
                }
                trigger="click"
              >
                <MessageOutlined
                  style={{
                    cursor: "pointer",
                    color: record.cancellationReason ? "#ff4d4f" : "#bfbfbf",
                  }}
                />
              </Popover>
            )}
            </Space>
            {!isMarriageEvent && <WhatsAppStatusCell record={record} />}
          </Space>
        );
      },
    },
    {
      title: t("eventRegistrations.table.currentStatus"),
      dataIndex: "checkedIn",
      key: "checkedIn",
      width: 140,
      render: (checkedIn, record) => {
        // Not checked in at all
        if (!checkedIn && !record.checkedOut) {
          return (
            <Badge
              status="default"
              text={t("eventRegistrations.table.notCheckedIn")}
            />
          );
        }

        // Currently checked in (inside venue)
        if (record.lastActionType === "checkin" || (checkedIn && !record.checkedOut)) {
          return (
            <div>
              <Badge
                status="success"
                text={<span style={{ color: '#52c41a', fontWeight: 500 }}>{t("eventRegistrations.table.checkedIn")}</span>}
              />
              {record.checkedInAt && (
                <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>
                  {moment(record.checkedInAt).format("HH:mm")}
                  {record.currentCycleNumber > 1 && (
                    <Tag color="blue" size="small" style={{ marginLeft: 4, fontSize: '10px' }}>
                      {t("eventRegistrations.table.visitNumber", { number: record.currentCycleNumber })}
                    </Tag>
                  )}
                </div>
              )}
            </div>
          );
        }

        // Checked out (outside venue)
        if (record.lastActionType === "checkout" || record.checkedOut) {
          return (
            <div>
              <Badge
                status="warning"
                text={<span style={{ color: '#fa8c16', fontWeight: 500 }}>{t("eventRegistrations.table.checkedOut")}</span>}
              />
              {record.checkedOutAt && (
                <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "2px" }}>
                  {moment(record.checkedOutAt).format("HH:mm")}
                  {record.currentCycleNumber > 1 && (
                    <Tag color="blue" size="small" style={{ marginLeft: 4, fontSize: '10px' }}>
                      {t("eventRegistrations.table.visitNumber", { number: record.currentCycleNumber })}
                    </Tag>
                  )}
                </div>
              )}
            </div>
          );
        }

        return <Badge status="default" text={t("eventRegistrations.table.unknown")} />;
      },
    },
    {
      title: t("eventRegistrations.table.checkinHistory"),
      key: "details",
      width: 90,
      render: (_, record) => {
        return (
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            style={{
              backgroundColor: '#0958d9',
              borderColor: 'var(--indigo-500)'
            }}
            onClick={() => handleViewHistory(record)}
          >
            {t("eventRegistrations.table.view")}
          </Button>
        );
      },
    },
    {
      title: t("eventRegistrations.table.totalTime"),
      dataIndex: "totalDurationInsideFormatted",
      key: "totalDuration",
      width: 100,
      responsive: ["lg"],
      render: (text) => text ? <Tag color="blue">{text}</Tag> : "-"
    },
    {
      title: t("eventRegistrations.table.registrationDate"),
      dataIndex: "registrationDate",
      key: "registrationDate",
      width: 150,
      responsive: ["lg"],
      render: (date) => moment(date).format("MMM DD, YYYY"),
    },
    {
      title: t("eventRegistrations.table.actions"),
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<UserOutlined />}
                onClick={() => handleViewDetails(record)}
              >
                {t("eventRegistrations.actionsMenu.viewDetails")}
              </Menu.Item>
              {/* Send Card option - for sending wallet download link */}
              <Menu.Item
                key="sendCard"
                icon={<DownloadOutlined />}
                onClick={() =>
                  requireFeature(
                    isMarriageEvent ? hasWhatsAppSupport : hasDigitalPassSupport,
                    isMarriageEvent
                      ? "eventRegistrations.actionsMenu.whatsappNotIncluded"
                      : "eventRegistrations.actionsMenu.digitalPassNotIncluded",
                    () => handleSendCard(record)
                  )
                }
              >
                {t("eventRegistrations.actionsMenu.sendCard")}
              </Menu.Item>
              <Menu.Item
                key="sendWhatsApp"
                icon={<WhatsAppOutlined style={{ color: hasWhatsAppSupport ? "#25D366" : undefined }} />}
                onClick={() => handleSendWhatsApp(record)}
                disabled={!hasWhatsAppSupport || (!!record.whatsAppDeliveryStatus && record.whatsAppDeliveryStatus !== "failed" && record.whatsAppDeliveryStatus !== "rejected")}
                title={!hasWhatsAppSupport ? t("eventRegistrations.actionsMenu.whatsappNotIncluded") : undefined}
              >
                {t("eventRegistrations.actionsMenu.sendWhatsapp")}
              </Menu.Item>
              <Menu.Item
                key="edit"
                icon={<EditOutlined />}
                onClick={() => handleEditParticipant(record)}
              >
                {t("eventRegistrations.actionsMenu.edit")}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                key="delete"
                danger
                onClick={() => handleDeleteParticipant(record)}
              >
                {t("eventRegistrations.actionsMenu.remove")}
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  // Wedding registrations have no Company/Type/VIP concept, and the check-in-only "Total Time"
  // detail moves into the existing check-in history "View" modal instead of taking up a table
  // column (that modal already shows it as "Total Event Time"). "Registration Date" is relabeled
  // "Invited Date" for weddings — same underlying value, just the guest-facing wording for a
  // private invitation flow instead of a self-service registration flow. Normal Events keep the
  // full column set unchanged.
  const columns = isMarriageEvent
    ? baseColumns
      .filter((col) => !["company", "type", "totalDuration"].includes(col.key))
      .map((col) => (col.key === "registrationDate" ? { ...col, title: t("eventRegistrations.table.invitedDate") } : col))
    : baseColumns;

  const handleRefresh = () => {
    if (id) {
      loadEventData();
      if (activeTab === "all") {
        setAllTabPagination((prev) => ({ ...prev, current: 1 }));
        loadRegistrations(1, 10000, searchText, statusFilter, "all");
      } else {
        loadRegistrations(
          pagination.current,
          pagination.pageSize,
          searchText,
          statusFilter,
          typeFilter
        );
      }
    }
  };

  // --- EDIT PARTICIPANT LOGIC ---
  const [editingParticipant, setEditingParticipant] = useState(null);
  // --- REMOVED: Company logo upload logic (now managed in company settings) ---

  // --- PICK FROM PHONE CONTACTS (Contact Picker API) ---
  // Only Chrome/Edge on Android currently implement this API. iOS Safari (and every
  // browser on iOS, since they all run on WebKit) has not implemented it, so the
  // button is hidden there and guests fall back to typing details manually.
  const supportsContactPicker =
    typeof navigator !== "undefined" &&
    "contacts" in navigator &&
    "ContactsManager" in window;

  const handlePickFromContacts = async () => {
    try {
      const props = ["name", "tel", "email"];
      const opts = { multiple: false };
      const [contact] = await navigator.contacts.select(props, opts);
      if (!contact) return;

      const fullName = (contact.name && contact.name[0]) || "";
      const [firstName, ...rest] = fullName.trim().split(/\s+/);
      const lastName = rest.join(" ");

      addVipForm.setFieldsValue({
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(contact.tel && contact.tel[0] && { phone: contact.tel[0] }),
        ...(contact.email && contact.email[0] && { email: contact.email[0] }),
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        message.error(t("eventRegistrations.errors.contactPickerFailed"));
      }
    }
  };

  const handleEditParticipant = (record) => {
    setEditingParticipant(record);
    addVipForm.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      phone: record.phone,
      company: record.company,
      jobTitle: record.jobTitle,
      city: record.city,
      country: record.country,
      companions: record.companions ?? 0,
      // ... map other fields
    });
    setAddVipModalVisible(true);
  };

  const handleCreateOrUpdateParticipant = async (values) => {
    setAddVipLoading(true);
    try {
      const token = localStorage.getItem("token");
      const isEdit = !!editingParticipant;

      const registrationData = {
        eventId: id,
        companyId: profile.companyId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        company: values.company,
        jobTitle: values.jobTitle,
        city: values.city,
        country: values.country,
        companions: values.companions ?? 0,
        registrationType: isEdit ? editingParticipant.registrationType : guestTypeToAdd,
        registrationSource: isEdit ? editingParticipant.registrationSource : "admin_manual",
      };

      // Create FormData
      const formData = new FormData();
      formData.append("registrationData", JSON.stringify(registrationData));

      // Logo upload removed - now managed in company settings

      let response;
      if (isEdit) {
        // PUT update (using with-file endpoint)
        response = await axios.put(
          `${BASE_URL}/EventRegistration/${editingParticipant.id}/with-file?companyId=${profile.companyId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        // POST create
        response = await axios.post(
          `${BASE_URL}/EventRegistration`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (response.data.success || response.status === 200) {
        message.success(
          isEdit
            ? t("eventRegistrations.messages.participantUpdated")
            : guestTypeToAdd === "vip"
              ? t("eventRegistrations.messages.vipParticipantAdded")
              : t("eventRegistrations.messages.participantAdded")
        );
        setAddVipModalVisible(false);
        addVipForm.resetFields();
        setEditingParticipant(null);
        setGuestTypeToAdd("vip");
        handleRefresh();
      } else {
        message.error(response.data.message || t("eventRegistrations.errors.operationFailed"));
      }
    } catch (error) {
      console.error("Error saving participant:", error);
      message.error(
        t("eventRegistrations.errors.saveParticipantFailed") +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setAddVipLoading(false);
    }
  };

  const calculateStatistics = () => {
    // Priority 1: Use summary stats from event object (Backend Source of Truth)
    // Priority 2: Use pagination info or local array counts as fallback
    
    const total = event?.totalRegistrations ?? (pagination.total || 0);
    const vipCount = event?.vipRegistrations ?? registrations.filter((reg) => reg.type === "vip").length;
    const regularCount = event?.regularRegistrations ?? registrations.filter((reg) => reg.type === "regular").length;
    
    // Checked in count (from registrations list)
    const checkedInCount = event?.totalCheckIns ?? registrations.filter((reg) => reg.checkedIn).length;
    
    // Revenue (still calculating from paged list for now)
    const totalRevenue = registrations.reduce((sum, reg) => sum + (reg.amount || 0), 0);

    return {
      total,
      vip: vipCount,
      regular: regularCount,
      checkedIn: checkedInCount,
      revenue: totalRevenue,
    };
  };

  const stats = calculateStatistics();

  // Mirrors the backend's own capacity check (EventRegistrationService.RegisterForEventAsync:
  // totalRegistrations >= capacityLimit) so the UI can pre-emptively hide the Add Guest action
  // instead of letting the organizer hit a "Failed to save participant: Event is at full
  // capacity" error after filling out the form. Only meaningful for Weddings, where
  // maxInvitations reflects a real paid limit (see Set 3's invitation-limit display fix).
  const invitationLimitReached = isMarriageEvent && maxInvitations != null && stats.total >= maxInvitations;

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    preserveSelectedRowKeys: true,
  };

  // Only real guests can be selected (the "All" tab also lists speakers and exhibitors).
  const isGuestRow = (record) => record.type === "regular" || record.type === "vip";

  // "Select all N guests": the table only holds one page, so ask the server for every matching guest id.
  const selectAllMatching = async (selectableTotal) => {
    if (activeTab === "all") {
      setSelectedRowKeys(filteredRegistrations.filter(isGuestRow).map((r) => r.id));
      return;
    }
    setSelectingAll(true);
    try {
      const dates = dateFilter;
      const params = new URLSearchParams({
        take: String(Math.max(selectableTotal, 1)),
        skip: "0",
        ...(searchText && { searchText }),
        ...(statusFilter !== "all" && !GUEST_FILTERS.includes(statusFilter) && { status: statusFilter }),
        ...(GUEST_FILTERS.includes(statusFilter) && { guestFilter: statusFilter }),
        registrationType: activeTab,
        ...(dates && dates[0] && { startDate: dates[0].format("YYYY-MM-DD") }),
        ...(dates && dates[1] && { endDate: dates[1].format("YYYY-MM-DD") }),
        companyId: profile.companyId,
      });
      const response = await axios.get(`${BASE_URL}/EventRegistration/event/${id}?${params}`);
      setSelectedRowKeys((response.data?.items || []).map((r) => r.id));
    } catch (error) {
      message.error(t("eventRegistrations.errors.loadRegistrationsFailed") + (error.response?.data?.message || error.message));
    } finally {
      setSelectingAll(false);
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedRowKeys([]);
  };

  // The selection controls above a list: how to start selecting, "select all", how many are
  // picked, and a way to extend the choice beyond the current page.
  const renderSelectionBar = (dataSource, selectableTotal) => {
    const pageIds = dataSource.filter(isGuestRow).map((r) => r.id);
    if (pageIds.length === 0) return null;
    const selectedOnPage = pageIds.filter((rid) => selectedRowKeys.includes(rid)).length;
    const allOnPage = selectedOnPage === pageIds.length;
    const everythingSelected = selectableTotal > 0 && selectedRowKeys.length >= selectableTotal;
    const moreThanThisPage = selectableTotal > pageIds.length;

    const togglePage = () =>
      setSelectedRowKeys((prev) =>
        allOnPage ? prev.filter((k) => !pageIds.includes(k)) : [...new Set([...prev, ...pageIds])]
      );

    // The "select everyone, not just this page" line (shown on phone and computer).
    const extendLine =
      selectedRowKeys.length > 0 && moreThanThisPage && (allOnPage || everythingSelected) ? (
        <div style={{ fontSize: 13, color: "#595959", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {everythingSelected
            ? t("eventRegistrations.selection.allSelected", { total: selectableTotal })
            : t("eventRegistrations.selection.pageAllSelected", { count: pageIds.length })}
          {everythingSelected ? (
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSelectedRowKeys([])}>
              {t("eventRegistrations.selection.clear")}
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              loading={selectingAll}
              onClick={() => selectAllMatching(selectableTotal)}
            >
              {t("eventRegistrations.selection.selectEverything", { total: selectableTotal })}
            </Button>
          )}
        </div>
      ) : null;

    if (isMobile) {
      if (!selectMode) {
        return (
          <div style={{ marginBottom: 12 }}>
            <Button icon={<CheckSquareOutlined />} onClick={() => setSelectMode(true)}>
              {t("eventRegistrations.selection.select")}
            </Button>
          </div>
        );
      }
      return (
        <div
          style={{
            marginBottom: 14,
            padding: "12px 14px",
            borderRadius: 10,
            background: "#f0f5ff",
            border: "1px solid #adc6ff",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <Checkbox indeterminate={selectedOnPage > 0 && !allOnPage} checked={allOnPage} onChange={togglePage}>
              {t("eventRegistrations.selection.selectAll")}
            </Checkbox>
            <Button type="text" onClick={exitSelectMode}>
              {t("eventRegistrations.selection.cancel")}
            </Button>
          </div>
          <div style={{ fontSize: 13, color: "#434343" }}>
            {t("eventRegistrations.selection.selectedOf", {
              count: selectedRowKeys.length,
              total: selectableTotal,
            })}
          </div>
          {extendLine}
        </div>
      );
    }

    // Computer: the table already has a checkbox on every row; this strip adds the "everyone" step.
    return extendLine ? (
      <div style={{ marginBottom: 12, padding: "10px 16px", borderRadius: 8, background: "#f6f8ff", border: "1px solid #d6e0ff" }}>
        {extendLine}
      </div>
    ) : null;
  };

  const renderBulkActionsBar = () => {
    if (selectedRowKeys.length === 0) return null;
    return (
      <div
        style={{
          marginBottom: 16,
          padding: '10px 16px',
          borderRadius: 6,
          backgroundColor: '#f0f5ff',
          border: '1px solid #adc6ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(isMobile && {
            position: 'fixed',
            insetInline: 8,
            bottom: 8,
            marginBottom: 0,
            zIndex: 1000,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          }),
        }}
      >
        <span>{t("eventRegistrations.bulkActions.selectedCount", { count: selectedRowKeys.length })}</span>
        <Button
          icon={<WhatsAppOutlined style={{ color: hasWhatsAppSupport ? "#25D366" : undefined }} />}
          onClick={() => handleBulkAction("whatsapp")}
          disabled={!hasWhatsAppSupport}
          loading={bulkWhatsAppLoading}
          title={!hasWhatsAppSupport ? t("eventRegistrations.actionsMenu.whatsappNotIncluded") : undefined}
        >
          {t("eventRegistrations.actionsMenu.sendWhatsapp")}
        </Button>
      </div>
    );
  };

  const activeFilterCount =
    (statusFilter !== "all" ? 1 : 0) + (typeFilter !== "all" ? 1 : 0) + (dateFilter ? 1 : 0);

  // Check-in details only mean something once the event has started (times are Gulf time, UTC+3).
  const eventStarted = (() => {
    if (!event?.startDate) return false;
    const time = /^\d{2}:\d{2}/.test(event.startTime || "") ? event.startTime.slice(0, 8) : "00:00:00";
    const start = Date.parse(`${String(event.startDate).slice(0, 10)}T${time.length === 5 ? time + ":00" : time}+03:00`);
    return Number.isNaN(start) ? false : Date.now() >= start;
  })();

  const openAddGuest = () => {
    setGuestTypeToAdd("regular");
    setAddVipModalVisible(true);
  };

  const renderEmptyState = () => (
    <RegistrationsEmptyState
      isWedding={isMarriageEvent}
      used={stats.total}
      max={maxInvitations ?? undefined}
      limitReached={invitationLimitReached}
      primaryColor={primaryColor}
      onAddGuest={openAddGuest}
      onImport={() => setImportModalVisible(true)}
      onAddVip={
        !isMarriageEvent && event?.enableVipRegistrations
          ? () => {
            setGuestTypeToAdd("vip");
            setAddVipModalVisible(true);
          }
          : undefined
      }
      onManageSpeakers={!isMarriageEvent && event?.enableSpeakers ? () => setActiveTab("speakers") : undefined}
      onManageExhibitors={!isMarriageEvent && event?.enableExhibitors ? () => setActiveTab("exhibitors") : undefined}
    />
  );

  // Mobile floating "add" action for whichever list tab is showing. Hidden while the empty state
  // is showing, since its own button is the primary action there.
  const fabConfig = !hasAnyData
    ? null
    : activeTab === "all" && !isMarriageEvent && !invitationLimitReached
      ? {
        icon: <PlusOutlined />,
        color: primaryColor,
        label: t("eventRegistrations.emptyState.addGuest"),
        menu: [
          { key: "guest", icon: <UserOutlined />, label: t("eventRegistrations.emptyState.addGuest"), onClick: openAddGuest },
          { key: "import", icon: <ContactsOutlined />, label: t("eventRegistrations.import.button"), onClick: () => setImportModalVisible(true) },
          ...(event?.enableVipRegistrations
            ? [{
              key: "vip",
              icon: <CrownOutlined />,
              label: t("eventRegistrations.addVipParticipant"),
              onClick: () => {
                setGuestTypeToAdd("vip");
                setAddVipModalVisible(true);
              },
            }]
            : []),
        ],
      }
    : activeTab === "regular" && !invitationLimitReached
      ? {
        icon: <PlusOutlined />,
        color: primaryColor,
        label: isMarriageEvent ? t("eventRegistrations.addGuest") : t("eventRegistrations.addRegularGuest"),
        menu: [
          {
            key: "guest",
            icon: <UserOutlined />,
            label: isMarriageEvent ? t("eventRegistrations.addGuest") : t("eventRegistrations.addRegularGuest"),
            onClick: openAddGuest,
          },
          { key: "import", icon: <ContactsOutlined />, label: t("eventRegistrations.import.button"), onClick: () => setImportModalVisible(true) },
        ],
      }
      : activeTab === "vip" && event?.enableVipRegistrations && !isMarriageEvent
        ? {
          icon: <CrownOutlined />,
          color: "#faad14",
          label: t("eventRegistrations.addVipParticipant"),
          onClick: () => {
            setGuestTypeToAdd("vip");
            setAddVipModalVisible(true);
          },
        }
        : null;

  // Helper function to build tabs based on event toggle settings
  const getTabItems = () => {
    const tabs = [];

    // Helper to transform speakers/exhibitors to registration-like format
    const transformEntityToRegistration = (entity, type) => {
      const report = entityCheckIns[entity.id];
      const sessions = report?.sessions || [];
      const lastSession = sessions.length ? sessions[sessions.length - 1] : null;
      const isInside = report?.currentStatus === 'inside';
      const hasLeft = report?.currentStatus === 'left';
      return {
      id: entity.id,
      name: `${entity.firstName || ''} ${entity.lastName || ''}`.trim() || entity.name || 'N/A',
      firstName: entity.firstName || '',
      lastName: entity.lastName || '',
      email: entity.email || 'N/A',
      phone: entity.phoneNumber || entity.phone || 'N/A',
      company: entity.company || 'N/A',
      position: entity.jobTitle || entity.position || 'N/A',
      type: type,
      vipPackages: [],
      status: 'confirmed',
      registrationDate: entity.createdAt || new Date().toISOString(),
      paymentStatus: 'paid',
      amount: 0,
      notes: entity.notes || '',
      avatar: entity.avatar || null,
      checkedIn: isInside || hasLeft || entity.checkedIn || false,
      checkedInAt: lastSession?.checkInTime || entity.checkedInAt || null,
      checkedInGate: entity.checkedInGate || null,
      checkedInBy: entity.checkedInBy || null,
      checkedOut: hasLeft || entity.checkedOut || false,
      checkedOutAt: hasLeft ? lastSession?.checkOutTime || null : entity.checkedOutAt || null,
      checkedOutGate: entity.checkedOutGate || null,
      checkedOutBy: entity.checkedOutBy || null,
      lastActionType: isInside ? 'checkin' : hasLeft ? 'checkout' : null,
      currentCycleNumber: sessions.length || null,
      totalDurationInside: null,
      totalDurationInsideFormatted: sessions.length ? report.totalEventTimeFormatted || null : null,
      firstCheckInTime: sessions[0]?.checkInTime || null,
      lastCheckOutTime: null,
      journeyLog: null,
      };
    };

    // Combine all registrations, speakers, and exhibitors for "All"
    const allEntities = [
      ...filteredRegistrations,
      ...(event?.speakers || []).map(s => transformEntityToRegistration(s, 'speaker')),
      ...(event?.exhibitors || []).map(e => transformEntityToRegistration(e, 'exhibitor')),
    ];
    const allStartIndex = (allTabPagination.current - 1) * allTabPagination.pageSize;
    const allEntitiesForCurrentPage = allEntities.slice(
      allStartIndex,
      allStartIndex + allTabPagination.pageSize
    );

    // Mobile gets a card list; tablet/desktop keep the table (fewer columns + tighter pager below lg).
    const renderList = (dataSource, pager) => (
      <>
        {renderSelectionBar(dataSource, pager.selectableTotal ?? pager.total)}
        {renderListBody(dataSource, pager)}
      </>
    );

    const renderListBody = (dataSource, pager) =>
      isMobile ? (
        <RegistrationCardList
          columns={columns}
          data={dataSource}
          loading={loading}
          selectMode={selectMode}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={setSelectedRowKeys}
          onView={handleViewDetails}
          onHistory={handleViewHistory}
          isWedding={isMarriageEvent}
          eventStarted={eventStarted}
          onSendWhatsApp={handleSendWhatsApp}
          onSendEmail={handleSendCard}
          canSend={hasWhatsAppSupport}
          pagination={{
            current: pager.current,
            pageSize: pager.pageSize,
            total: pager.total,
            onChange: pager.onChange,
          }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          size={isDesktop ? "default" : "small"}
          pagination={{
            current: pager.current,
            pageSize: pager.pageSize,
            total: pager.total,
            size: isDesktop ? "default" : "small",
            showSizeChanger: true,
            showQuickJumper: isDesktop,
            showTotal: pager.showTotal,
            onChange: pager.onChange,
            onShowSizeChange: pager.onShowSizeChange,
          }}
          rowSelection={rowSelection}
          loading={loading}
          scroll={{ x: isDesktop ? 1300 : 900 }}
        />
      );

    // Toolbar row above each list. On mobile Refresh lives in the filter bar's "more" menu and
    // "Add" is a floating button, so the row disappears entirely.
    const renderToolbar = (addButton) => {
      if (isMobile) return null;
      return (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {addButton || <span />}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{ borderRadius: '4px', marginInlineStart: 'auto' }}
          >
            {t("eventRegistrations.filters.refresh")}
          </Button>
        </div>
      );
    };

    // "All Registrations" combines guests, speakers and exhibitors; weddings only have guests.
    if (!isMarriageEvent) tabs.push({
      key: "all",
      label: (
        <Space>
          <UserOutlined />
          <span>{t("eventRegistrations.tabs.all")}</span>
          <Badge count={event?.totalRegistrations || 0} showZero />
        </Space>
      ),
      children: !hasAnyData ? renderEmptyState() : (
        <div>
          {renderBulkActionsBar()}
          {renderToolbar(null)}
          {renderList(allEntitiesForCurrentPage, {
            current: allTabPagination.current,
            pageSize: allTabPagination.pageSize,
            total: allEntities.length,
            selectableTotal: filteredRegistrations.filter(isGuestRow).length,
            showTotal: (total, range) =>
              t("eventRegistrations.tabs.registrationsSuffix", { start: range[0], end: range[1], total }),
            onChange: (page, pageSize) => setAllTabPagination({ current: page, pageSize }),
            onShowSizeChange: (current, size) => setAllTabPagination({ current: 1, pageSize: size }),
          })}
        </div>
      ),
    });

    // Always show "Regular Guests"
    tabs.push({
      key: "regular",
      label: (
        <Space>
          <UserOutlined style={{ color: primaryColor }} />
          <span>{isMarriageEvent ? t("eventRegistrations.tabs.guests") : t("eventRegistrations.tabs.regular")}</span>
          <Badge
            count={event?.regularRegistrations || 0}
            showZero
            style={{ backgroundColor: primaryColor }}
          />
        </Space>
      ),
      children: !hasAnyData ? renderEmptyState() : (
        <div>
          {renderBulkActionsBar()}
          {invitationLimitReached && (
            <div
              style={{
                marginBottom: 16,
                padding: '10px 16px',
                borderRadius: 6,
                backgroundColor: '#fff2e8',
                border: '1px solid #ffbb96',
                color: '#d4380d',
                fontWeight: 500,
              }}
            >
              {t("eventRegistrations.invitationLimitReached", { current: stats.total, max: maxInvitations })}
            </div>
          )}
          {renderToolbar(
            !invitationLimitReached && (
              <Space wrap>
                <Button
                  type="primary"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                  onClick={openAddGuest}
                >
                  {isMarriageEvent ? t("eventRegistrations.addGuest") : t("eventRegistrations.addRegularGuest")}
                </Button>
                <Button icon={<ContactsOutlined />} onClick={() => setImportModalVisible(true)}>
                  {t("eventRegistrations.import.button")}
                </Button>
              </Space>
            )
          )}
          {renderList(filteredRegistrations, {
            total: event?.regularRegistrations || 0,
            current: pagination.current,
            pageSize: pagination.pageSize,
            showTotal: (total, range) => t(isMarriageEvent ? "eventRegistrations.tabs.guestsSuffix" : "eventRegistrations.tabs.regularGuestsSuffix", { start: range[0], end: range[1], total }),
            onChange: (page, pageSize) => loadRegistrations(page, pageSize, searchText, statusFilter, "regular"),
            onShowSizeChange: (current, pageSize) => loadRegistrations(1, pageSize, searchText, statusFilter, "regular"),
          })}
        </div>
      ),
    });

    // Conditional: Add VIP tab only if enabled — and never for Wedding events, which have no
    // VIP concept at all (guests are all "regular", invited privately by the host).
    if (event?.enableVipRegistrations && !isMarriageEvent) {
      tabs.push({
        key: "vip",
        label: (
          <Space size={isMobile ? 4 : 8}>
            <CrownOutlined style={{ color: "#faad14" }} />
            <span>{t("eventRegistrations.tabs.vip")}</span>
            <Badge
              count={event?.vipRegistrations || 0}
              showZero
              style={{ backgroundColor: "#faad14" }}
            />
          </Space>
        ),
        children: (
          <div>
            {renderBulkActionsBar()}
            {renderToolbar(
              <Button
                type="primary"
                icon={<CrownOutlined />}
                onClick={() => {
                  setGuestTypeToAdd("vip");
                  setAddVipModalVisible(true);
                }}
              >
                {t("eventRegistrations.addVipParticipant")}
              </Button>
            )}
            {renderList(filteredRegistrations, {
              total: event?.vipRegistrations || 0,
              current: pagination.current,
              pageSize: pagination.pageSize,
              showTotal: (total, range) => t("eventRegistrations.tabs.vipGuestsSuffix", { start: range[0], end: range[1], total }),
              onChange: (page, pageSize) => loadRegistrations(page, pageSize, searchText, statusFilter, "vip"),
              onShowSizeChange: (current, pageSize) => loadRegistrations(1, pageSize, searchText, statusFilter, "vip"),
            })}
          </div>
        ),
      });
    }

    // Conditional: Add Speakers tab only if enabled
    if (event?.enableSpeakers) {
      tabs.push({
        key: "speakers",
        label: (
          <Space size={isMobile ? 4 : 8}>
            <UserOutlined style={{ color: "#1890ff" }} />
            <span>{t("eventRegistrations.tabs.speakers")}</span>
            <Badge
              count={event.speakers?.length || 0}
              showZero
              style={{ backgroundColor: "#1890ff" }}
            />
          </Space>
        ),
        children: (
          <SpeakerManagement
            eventId={event.id}
            speakers={event.speakers || []}
            onUpdate={loadEventData}
          />
        ),
      });
    }
    // Conditional: Add Exhibitors tab only if enabled
    if (event?.enableExhibitors) {
      tabs.push({
        key: "exhibitors",
        label: (
          <Space size={isMobile ? 4 : 8}>
            <ShopOutlined style={{ color: "#52c41a" }} />
            <span>{t("eventRegistrations.tabs.exhibitors")}</span>
            <Badge
              count={event.exhibitors?.length || 0}
              showZero
              style={{ backgroundColor: "#52c41a" }}
            />
          </Space>
        ),
        children: (
          <ExhibitorManagement
            eventId={event.id}
            exhibitors={event.exhibitors || []}
            onUpdate={loadEventData}
          />
        ),
      });
    }

    // Weddings have a single "Guests" tab (the "regular" tab) - no combined "All" view.
    return isMarriageEvent ? tabs.filter((tab) => tab.key !== "all") : tabs;
  };

  console.log("EventRegistrationsPage rendering with event:", event);

  // Show error state if event not found
  if (eventNotFound) {
    return (
      <Layout className="min-h-full bg-transparent">
        <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("eventRegistrations.eventNotFound.title")}
            </h3>
            <p className="text-gray-500 mb-4">
              {t("eventRegistrations.eventNotFound.description")}
            </p>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/events")}

            >
              {t("eventRegistrations.backToEvents")}
            </Button>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-3 sm:py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/events")}
            >
              {isMobile ? null : t("eventRegistrations.backToEvents")}
            </Button>
            <div style={{ minWidth: 0 }}>
              <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-white break-words">
                {event?.title || t("eventRegistrations.pageTitle")}
              </h1>
            </div>
          </div>
        </div>
      </header>
      <Content className="rounded-lg px-2 py-3 shadow bg-white sm:px-6 sm:py-6">
        <div className="bg-white">
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
            <CalendarOutlined className="mr-2" />
            {event?.startDate && event?.endDate ? (
              <>
                {formatDate(String(event.startDate).slice(0, 10), i18n.language, event.calendarPreference, event.hijriAdjustmentDays || 0)}
                {String(event.endDate).slice(0, 10) !== String(event.startDate).slice(0, 10) && (
                  <>
                    {" - "}
                    {formatDate(String(event.endDate).slice(0, 10), i18n.language, event.calendarPreference, event.hijriAdjustmentDays || 0)}
                  </>
                )}
              </>
            ) : (
              t("eventRegistrations.loading")
            )}
            {event?.location && ` • ${event.location}`}
          </p>

          {/* Statistics */}
          {hasAnyData && (
          <div
            className="mb-4 sm:mb-6"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: isMobile ? 8 : 16,
            }}
          >
            {(isMarriageEvent
              ? [
                {
                  title: maxInvitations ? t("eventRegistrations.stats.invitationsUsed") : t("eventRegistrations.stats.totalRegistrations"),
                  value: stats.total,
                  suffix: maxInvitations ? `/ ${maxInvitations}` : undefined,
                  prefix: <UserOutlined />,
                  color: primaryColor,
                },
                {
                  title: t("eventRegistrations.stats.confirmed"),
                  value: event?.acceptedRegistrations ?? 0,
                  prefix: <CheckCircleOutlined />,
                  color: "#52c41a",
                },
                {
                  title: t("eventRegistrations.stats.pending"),
                  value: event?.pendingRegistrations ?? 0,
                  prefix: <ClockCircleOutlined />,
                  color: "#fa8c16",
                },
                {
                  title: t("eventRegistrations.stats.declined"),
                  value: event?.rejectedRegistrations ?? 0,
                  prefix: <CloseCircleOutlined />,
                  color: "#f5222d",
                },
                {
                  title: t("eventRegistrations.stats.checkedIn"),
                  value: stats.checkedIn,
                  prefix: <IdcardOutlined />,
                  color: "#1677ff",
                },
              ]
              : [
                {
                  title: maxInvitations ? t("eventRegistrations.stats.invitationsUsed") : t("eventRegistrations.stats.totalRegistrations"),
                  value: stats.total,
                  suffix: maxInvitations ? `/ ${maxInvitations}` : undefined,
                  prefix: <UserOutlined />,
                  color: primaryColor,
                },
                {
                  title: t("eventRegistrations.stats.vipRegistrations"),
                  value: stats.vip,
                  prefix: <CrownOutlined />,
                  color: "#faad14",
                },
                {
                  title: t("eventRegistrations.stats.regularRegistrations"),
                  value: stats.regular,
                  prefix: <UserOutlined />,
                  color: primaryColor,
                },
                {
                  title: t("eventRegistrations.stats.totalRevenue"),
                  value: stats.revenue,
                  prefix: "$",
                  precision: 0,
                  color: "#52c41a",
                },
              ]
            ).map(({ color, ...stat }) => (
              <Card key={stat.title} size={isMobile ? "small" : "default"}>
                <Statistic
                  {...stat}
                  valueStyle={{ color, fontSize: isMobile ? 20 : undefined }}
                />
              </Card>
            ))}
          </div>
          )}

          {hasAnyData && isMarriageEvent && (
            <NextStepsChecklist
              eventId={id}
              total={stats.total}
              pending={stats.pending}
              hasGates={(event?.gates?.length || 0) > 0}
              refreshKey={`${stats.total}-${stats.pending}`}
              onAdd={openAddGuest}
              onSend={() => setSendInvitesVisible(true)}
              onShowWaiting={() => handleStatusChange("pending")}
              onGates={() => navigate(`/event-form/${id}?section=gates`)}
              primaryColor={primaryColor}
              isMobile={isMobile}
            />
          )}

          {/* Wedding: the three things a host does most, always in view */}
          {hasAnyData && isMarriageEvent && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <Button
                type="primary"
                icon={<UserOutlined />}
                disabled={invitationLimitReached}
                onClick={openAddGuest}
                style={{ flex: isMobile ? "1 1 140px" : "0 0 auto", backgroundColor: primaryColor, borderColor: primaryColor }}
              >
                {t("eventRegistrations.emptyState.addGuest")}
              </Button>
              <Button
                icon={<ContactsOutlined />}
                disabled={invitationLimitReached}
                onClick={() => setImportModalVisible(true)}
                style={{ flex: isMobile ? "1 1 140px" : "0 0 auto" }}
              >
                {t("eventRegistrations.import.button")}
              </Button>
              <Button
                icon={<WhatsAppOutlined style={{ color: "#25D366" }} />}
                onClick={() => setSendInvitesVisible(true)}
                style={{ flex: isMobile ? "1 1 140px" : "0 0 auto" }}
              >
                {t("eventRegistrations.sendInvites.button")}
              </Button>
              <Tooltip
                title={
                  !hasWhatsAppSupport
                    ? t("eventRegistrations.actionsMenu.whatsappNotIncluded")
                    : !eventHasEnded
                    ? t("eventRegistrations.sendThankYou.availableAfterEnd")
                    : undefined
                }
              >
                <Button
                  icon={<WhatsAppOutlined style={{ color: hasWhatsAppSupport && eventHasEnded ? "#25D366" : undefined }} />}
                  onClick={handleSendThankYou}
                  disabled={!hasWhatsAppSupport || !eventHasEnded}
                  loading={thankYouLoading}
                  style={{ flex: isMobile ? "1 1 140px" : "0 0 auto" }}
                >
                  {t("eventRegistrations.sendThankYou.button")}
                </Button>
              </Tooltip>
              <Tooltip title={!hasWhatsAppSupport ? t("eventRegistrations.actionsMenu.whatsappNotIncluded") : t("eventRegistrations.sendThankYou.editTooltip")}>
                <Button
                  icon={<EditOutlined />}
                  onClick={openThankYouEdit}
                  disabled={!hasWhatsAppSupport}
                  style={{ flex: isMobile ? "1 1 140px" : "0 0 auto" }}
                >
                  {t("eventRegistrations.sendThankYou.editButton")}
                </Button>
              </Tooltip>
            </div>
          )}

          {/* Filters */}
          {!hasAnyData ? null : isMobile ? (
            <div className="mb-3">
              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  placeholder={t("eventRegistrations.filters.searchPlaceholder")}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={handleSearchChange}
                  allowClear
                  size="large"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
                  <Button
                    size="large"
                    icon={<FilterOutlined />}
                    onClick={() => setFilterDrawerOpen(true)}
                    aria-label={t("eventRegistrations.filters.filters")}
                  />
                </Badge>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      { key: "refresh", icon: <ReloadOutlined />, label: t("eventRegistrations.filters.refresh"), onClick: handleRefresh },
                      {
                        key: "export",
                        icon: <ExportOutlined />,
                        label: t("eventRegistrations.filters.export"),
                        onClick: handleExport,
                      },
                    ],
                  }}
                >
                  <Button size="large" icon={<MoreOutlined />} loading={loading} />
                </Dropdown>
              </div>
              {activeFilterCount > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {statusFilter !== "all" && (
                    <Tag closable onClose={() => handleStatusChange("all")}>
                      {t(`eventRegistrations.filters.${statusFilter}`)}
                    </Tag>
                  )}
                  {typeFilter !== "all" && (
                    <Tag closable onClose={() => handleTypeChange("all")}>
                      {t(`eventRegistrations.filters.${typeFilter}`)}
                    </Tag>
                  )}
                  {dateFilter && (
                    <Tag closable onClose={() => handleDateChange(null)}>
                      {dateFilter[0]?.format("MMM DD")} – {dateFilter[1]?.format("MMM DD")}
                    </Tag>
                  )}
                </div>
              )}
            </div>
          ) : (
          <Card className="mb-4">
            {/* Search and Filters Row */}
            <Row gutter={[16, 16]} className="mb-4">
              <Col xs={24} sm={24} md={24} lg={8} xl={7}>
                <Input
                  placeholder={t("eventRegistrations.filters.searchPlaceholder")}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={handleSearchChange}
                  allowClear
                />
              </Col>
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Select
                  value={statusFilter}
                  onChange={handleStatusChange}
                  className="w-full"
                  placeholder={t("eventRegistrations.filters.statusPlaceholder")}
                >
                  {statusFilterOptions.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </Col>
              {!isMarriageEvent && (
              <Col xs={12} sm={8} md={6} lg={4} xl={4}>
                <Select
                  value={typeFilter}
                  onChange={handleTypeChange}
                  className="w-full"
                  placeholder={t("eventRegistrations.filters.typePlaceholder")}
                >
                  <Option value="all">{t("eventRegistrations.filters.allTypes")}</Option>
                  <Option value="regular">{t("eventRegistrations.filters.regular")}</Option>
                  {!isMarriageEvent && <Option value="vip">{t("eventRegistrations.filters.vip")}</Option>}
                  <Option value="speaker">{t("eventRegistrations.filters.speaker")}</Option>
                  <Option value="exhibitor">{t("eventRegistrations.filters.exhibitor")}</Option>
                </Select>
              </Col>
              )}
              <Col xs={24} sm={8} md={8} lg={5} xl={6}>
                <RangePicker
                  value={dateFilter}
                  onChange={handleDateChange}
                  placeholder={[t("eventRegistrations.filters.startDate"), t("eventRegistrations.filters.endDate")]}
                  className="w-full"
                />
              </Col>
              <Col xs={12} sm={8} md={4} lg={3} xl={3}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                  className="w-full"
                >
                  {t("eventRegistrations.filters.refresh")}
                </Button>
              </Col>
            </Row>

            {/* Quick tab switchers are desktop-only (the tab bar below does the same job on
                smaller screens); Export stays on every non-mobile layout. */}
            <Row gutter={[8, 8]} justify="space-between" align="middle" className="mt-4">
              <Col>
                {isDesktop && !isMarriageEvent && (
                  <Space>
                    <Button
                      type={activeTab === "all" ? "primary" : "default"}
                      icon={<UserOutlined />}
                      onClick={() => setActiveTab("all")}
                      size="small"
                      style={
                        activeTab === "all"
                          ? {
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                          }
                          : {}
                      }
                    >
                      {t("eventRegistrations.filters.all", { count: event?.totalRegistrations || 0 })}
                    </Button>
                    <Button
                      type={activeTab === "regular" ? "primary" : "default"}
                      icon={<UserOutlined />}
                      onClick={() => setActiveTab("regular")}
                      size="small"
                      style={{
                        backgroundColor:
                          activeTab === "regular" ? primaryColor : "transparent",
                        borderColor: primaryColor,
                        color: activeTab === "regular" ? "white" : primaryColor,
                      }}
                    >
                      {t("eventRegistrations.filters.regularCount", { count: event?.regularRegistrations || 0 })}
                    </Button>
                    {!isMarriageEvent && (
                      <Button
                        type={activeTab === "vip" ? "primary" : "default"}
                        icon={<CrownOutlined />}
                        onClick={() => setActiveTab("vip")}
                        size="small"
                        style={{
                          backgroundColor:
                            activeTab === "vip" ? "#faad14" : "transparent",
                          borderColor: "#faad14",
                          color: activeTab === "vip" ? "white" : "#faad14",
                        }}
                      >
                        {t("eventRegistrations.filters.vipCount", { count: event?.vipRegistrations || 0 })}
                      </Button>
                    )}
                  </Space>
                )}
              </Col>
              <Col>
                <Button
                  icon={<ExportOutlined />}
                  onClick={handleExport}
                >
                  {t("eventRegistrations.filters.export")}
                </Button>
              </Col>
            </Row>
          </Card>
          )}

          {/* Registrations Tabs. Weddings only have the Guests list, so no tab bar. */}
          {isMarriageEvent ? (
            isMobile ? (
              getTabItems().find((tab) => tab.key === "regular")?.children
            ) : (
              <Card>{getTabItems().find((tab) => tab.key === "regular")?.children}</Card>
            )
          ) : isMobile ? (
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setSelectMode(false);
                setSelectedRowKeys([]);
              }}
              items={getTabItems()}
              size="small"
              tabBarGutter={16}
            />
          ) : (
            <Card>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={getTabItems()}
              />
            </Card>
          )}

          {/* Mobile: floating add button (hidden while the bulk selection bar is showing) */}
          {isMobile && fabConfig && selectedRowKeys.length === 0 && (
            <Dropdown
              disabled={!fabConfig.menu}
              trigger={["click"]}
              placement="topRight"
              menu={{ items: fabConfig.menu || [] }}
            >
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={fabConfig.icon}
              onClick={fabConfig.menu ? undefined : fabConfig.onClick}
              aria-label={fabConfig.label}
              style={{
                position: "fixed",
                insetInlineEnd: 20,
                bottom: 20,
                zIndex: 1000,
                width: 56,
                height: 56,
                fontSize: 22,
                backgroundColor: fabConfig.color,
                borderColor: fabConfig.color,
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              }}
            />
            </Dropdown>
          )}

          {/* Mobile filters bottom sheet */}
          <Drawer
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            placement="bottom"
            height="auto"
            title={t("eventRegistrations.filters.filters")}
            extra={
              <Button
                type="link"
                disabled={activeFilterCount === 0}
                onClick={() => {
                  if (statusFilter !== "all") handleStatusChange("all");
                  if (typeFilter !== "all") handleTypeChange("all");
                  if (dateFilter) handleDateChange(null);
                }}
              >
                {t("eventRegistrations.filters.reset")}
              </Button>
            }
            footer={
              <Button type="primary" block size="large" onClick={() => setFilterDrawerOpen(false)}>
                {t("eventRegistrations.filters.done")}
              </Button>
            }
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                size="large"
                style={{ width: "100%" }}
                placeholder={t("eventRegistrations.filters.statusPlaceholder")}
              >
                {statusFilterOptions.map((o) => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
              {!isMarriageEvent && (
              <Select
                value={typeFilter}
                onChange={handleTypeChange}
                size="large"
                style={{ width: "100%" }}
                placeholder={t("eventRegistrations.filters.typePlaceholder")}
              >
                <Option value="all">{t("eventRegistrations.filters.allTypes")}</Option>
                <Option value="regular">{t("eventRegistrations.filters.regular")}</Option>
                {!isMarriageEvent && <Option value="vip">{t("eventRegistrations.filters.vip")}</Option>}
                <Option value="speaker">{t("eventRegistrations.filters.speaker")}</Option>
                <Option value="exhibitor">{t("eventRegistrations.filters.exhibitor")}</Option>
              </Select>
              )}
              <RangePicker
                value={dateFilter}
                onChange={handleDateChange}
                size="large"
                style={{ width: "100%" }}
                placeholder={[t("eventRegistrations.filters.startDate"), t("eventRegistrations.filters.endDate")]}
              />
            </Space>
          </Drawer>

          <SendInvitationsModal
            open={sendInvitesVisible}
            onClose={() => setSendInvitesVisible(false)}
            onDone={handleRefresh}
            onChooseGuests={() => {
              setSelectMode(true);
              message.info(t("eventRegistrations.sendInvites.pickHint"));
            }}
            eventId={id}
            invitedBy={event?.invitedBy}
            hasWhatsAppSupport={hasWhatsAppSupport}
            primaryColor={primaryColor}
            isMobile={isMobile}
          />

          <Modal
            title={t("eventRegistrations.sendThankYou.editTitle")}
            open={thankYouEditOpen}
            onCancel={() => setThankYouEditOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setThankYouEditOpen(false)}>
                {t("eventRegistrations.sendThankYou.cancel")}
              </Button>,
              <Button key="save" type="primary" loading={thankYouTextSaving} onClick={handleSaveThankYouText}>
                {t("eventRegistrations.sendThankYou.save")}
              </Button>,
            ]}
          >
            <ThankYouMessagePreview
              editable
              displayTitle={event?.title}
              thankYouText={thankYouTextDraft}
              defaultThankYouText={DEFAULT_THANK_YOU_TEXT}
              onChangeThankYouText={setThankYouTextDraft}
            />
          </Modal>

          <ImportGuestsModal
            open={importModalVisible}
            onClose={() => setImportModalVisible(false)}
            onDone={handleRefresh}
            eventId={id}
            isWedding={isMarriageEvent}
            enableVip={!!event?.enableVipRegistrations}
            primaryColor={primaryColor}
            isMobile={isMobile}
          />

          {/* Registration Detail Modal */}
          <Modal
            title={isMarriageEvent ? t("eventRegistrations.detailModal.guestTitle") : t("eventRegistrations.detailModal.title")}
            open={detailModalVisible}
            onCancel={() => setDetailModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setDetailModalVisible(false)}>
                {t("eventRegistrations.detailModal.close")}
              </Button>,
              <Button
                key="sendCard"
                type="primary"
                style={{
                  backgroundColor: primaryColor,
                  borderColor: primaryColor,
                }}
                icon={isMarriageEvent ? <WhatsAppOutlined /> : <DownloadOutlined />}
                onClick={() =>
                  requireFeature(
                    isMarriageEvent ? hasWhatsAppSupport : hasDigitalPassSupport,
                    isMarriageEvent
                      ? "eventRegistrations.actionsMenu.whatsappNotIncluded"
                      : "eventRegistrations.actionsMenu.digitalPassNotIncluded",
                    () => handleSendCard(selectedRegistration)
                  )
                }
              >
                {isMarriageEvent
                  ? t("eventRegistrations.detailModal.sendInvitation")
                  : t("eventRegistrations.detailModal.sendCard")}
              </Button>,
            ]}
            width={isMobile ? "calc(100vw - 16px)" : 600}
            styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
          >
            {selectedRegistration && (
              <div>
                <div className="flex items-center mb-4">
                  <Avatar
                    size={64}
                    src={selectedRegistration.avatar}
                    icon={<UserOutlined />}
                    className={
                      selectedRegistration.type === "vip"
                        ? "border-2 border-yellow-400"
                        : ""
                    }
                  />
                  <div style={{ marginInlineStart: 16, minWidth: 0 }}>
                    <h3 className="text-lg font-medium flex items-center">
                      {selectedRegistration.name}
                      {selectedRegistration.type === "vip" && (
                        <CrownOutlined className="text-yellow-500 ml-2" />
                      )}
                    </h3>
                    {!isMarriageEvent && (
                      <>
                        <p className="text-gray-600">
                          {selectedRegistration.company}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedRegistration.position}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <Descriptions column={1} bordered size="small">
                  {(!isMarriageEvent || (selectedRegistration.email && selectedRegistration.email !== "N/A")) && (
                    <Descriptions.Item label={t("eventRegistrations.detailModal.email")}>
                      {selectedRegistration.email}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t("eventRegistrations.detailModal.phone")}>
                    {selectedRegistration.phone}
                  </Descriptions.Item>
                  {selectedRegistration.responseChangeCount > 0 && (
                    <Descriptions.Item label={t("eventRegistrations.responseChanged.detailLabel")}>
                      {t("eventRegistrations.responseChanged.detailValue", {
                        count: selectedRegistration.responseChangeCount,
                        time: selectedRegistration.responseChangedAt
                          ? moment(selectedRegistration.responseChangedAt).format("MMM DD, YYYY HH:mm")
                          : "",
                      })}
                    </Descriptions.Item>
                  )}
                  {(selectedRegistration.companions ?? 0) > 0 && (
                    <Descriptions.Item label={t("eventRegistrations.addEditModal.companions")}>
                      {i18n.language?.startsWith("ar")
                        ? toArabicDigits(selectedRegistration.companions)
                        : selectedRegistration.companions}
                    </Descriptions.Item>
                  )}
                  {!isMarriageEvent && (
                    <Descriptions.Item label={t("eventRegistrations.detailModal.registrationType")}>
                      <Tag
                        color={
                          selectedRegistration.type === "vip" ? "gold" : "blue"
                        }
                      >
                        {selectedRegistration.type.toUpperCase()}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {selectedRegistration.vipPackages.length > 0 && (
                    <Descriptions.Item label={t("eventRegistrations.detailModal.vipPackages")}>
                      {selectedRegistration.vipPackages.map((pkg, index) => (
                        <Tag key={index} color="orange" className="mb-1">
                          {pkg}
                        </Tag>
                      ))}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t("eventRegistrations.detailModal.status")}>
                    <Tag color={getStatusColor(selectedRegistration.status)}>
                      {selectedRegistration.status.toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  {(selectedRegistration.cancellationReason || selectedRegistration.status === "cancelled") && (
                    <Descriptions.Item label={t("eventRegistrations.table.cancellationReasonTitle")}>
                      {selectedRegistration.cancellationReason || t("eventRegistrations.table.cancellationReasonMissing")}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label={isMarriageEvent ? t("eventRegistrations.table.invitedDate") : t("eventRegistrations.detailModal.registrationDate")}
                  >
                    {moment(selectedRegistration.registrationDate).format(
                      "MMMM DD, YYYY"
                    )}
                  </Descriptions.Item>
                  {selectedRegistration.notes && (
                    <Descriptions.Item label={t("eventRegistrations.detailModal.notes")}>
                      {selectedRegistration.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}
          </Modal>

          {/* Check-in History Detail Modal */}
          <Modal
            title={
              <Space>
                <ClockCircleOutlined />
                <span>{t("eventRegistrations.historyModal.titlePrefix")} {historyRegistration ? `— ${historyRegistration.name}` : ''}</span>
              </Space>
            }
            open={historyModalVisible}
            onCancel={() => {
              setHistoryModalVisible(false);
              setHistoryData(null);
              setHistoryRegistration(null);
            }}
            footer={[
              <Button key="close" onClick={() => setHistoryModalVisible(false)}>
                {t("eventRegistrations.historyModal.close")}
              </Button>,
            ]}
            width={isMobile ? "calc(100vw - 16px)" : 720}
          >
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '16px', color: '#8c8c8c' }}>{t("eventRegistrations.historyModal.loading")}</div>
              </div>
            ) : historyData ? (
              <div>
                {/* Summary Row */}
                <Row gutter={16} style={{ marginBottom: 20 }}>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 4 }}>{t("eventRegistrations.historyModal.currentStatus")}</div>
                      <Tag
                        color={
                          historyData.currentStatus === 'inside' ? 'green'
                            : historyData.currentStatus === 'left' ? 'orange'
                              : 'default'
                        }
                        style={{ fontSize: '13px', padding: '2px 12px' }}
                      >
                        {historyData.currentStatus === 'inside' ? t("eventRegistrations.historyModal.insideVenue")
                          : historyData.currentStatus === 'left' ? t("eventRegistrations.historyModal.leftVenue")
                            : t("eventRegistrations.historyModal.notArrived")}
                      </Tag>
                      {historyData.currentStatus === 'inside' && historyData.currentZoneName && (
                        <div style={{ fontSize: '12px', color: '#52c41a', marginTop: 6, fontWeight: 500 }}>
                          📍 {historyData.currentZoneName}
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 4 }}>{t("eventRegistrations.historyModal.totalEventTime")}</div>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: primaryColor }}>
                        {historyData.totalEventTimeFormatted || '—'}
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 4 }}>{t("eventRegistrations.historyModal.totalSessions")}</div>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: '#1890ff' }}>
                        {historyData.sessions?.length || 0}
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* Zone Breakdown */}
                {historyData.zoneBreakdown && historyData.zoneBreakdown.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: 8 }}>
                      <EnvironmentOutlined style={{ marginRight: 6 }} />
                      {t("eventRegistrations.historyModal.zoneBreakdown")}
                    </div>
                    <Row gutter={[8, 8]}>
                      {historyData.zoneBreakdown.map((zone, idx) => (
                        <Col key={idx} span={12}>
                          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 500 }}>{zone.zoneName}</div>
                                <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                  {t("eventRegistrations.historyModal.visitsSuffix", { count: zone.visitCount })}
                                </div>
                              </div>
                              <Tag color="blue">{zone.totalTime || `${Math.round(zone.totalMinutes)}m`}</Tag>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Session Timeline */}
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: 12 }}>
                    <CalendarOutlined style={{ marginRight: 6 }} />
                    {t("eventRegistrations.historyModal.timeline")}
                  </div>
                  {historyData.sessions && historyData.sessions.length > 0 ? (
                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {historyData.sessions.map((session, idx) => {
                        const isActive = session.status === 'active';
                        const borderColor = isActive ? '#52c41a' : session.status === 'orphaned' ? '#ff4d4f' : '#d9d9d9';
                        return (
                          <div
                            key={idx}
                            style={{
                              border: `1px solid ${borderColor}`,
                              borderLeft: `4px solid ${borderColor}`,
                              borderRadius: 6,
                              padding: '10px 14px',
                              marginBottom: 8,
                              backgroundColor: isActive ? '#f6ffed' : '#fff',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <Space>
                                <Tag color={isActive ? 'green' : session.status === 'orphaned' ? 'red' : 'default'} style={{ margin: 0 }}>
                                  #{session.sessionNumber}
                                </Tag>
                                {session.zoneName && (
                                  <span style={{ fontSize: '12px', color: '#595959' }}>
                                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                                    {session.zoneName}
                                  </span>
                                )}
                              </Space>
                              {session.duration && (
                                <Tag color="blue" style={{ margin: 0 }}>
                                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                                  {session.duration}
                                </Tag>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 24, fontSize: '12px', color: '#595959' }}>
                              <div>
                                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                                <strong>{t("eventRegistrations.historyModal.in")}</strong> {moment(session.checkInTime).format("MMM DD, HH:mm:ss")}
                                {session.enteredGate && <span style={{ color: '#999' }}> {t("eventRegistrations.historyModal.via", { gate: session.enteredGate })}</span>}
                              </div>
                              {session.checkOutTime ? (
                                <div>
                                  <CloseCircleOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
                                  <strong>{t("eventRegistrations.historyModal.out")}</strong> {moment(session.checkOutTime).format("MMM DD, HH:mm:ss")}
                                  {session.exitedGate && <span style={{ color: '#999' }}> {t("eventRegistrations.historyModal.via", { gate: session.exitedGate })}</span>}
                                </div>
                              ) : (
                                <Tag color="green" style={{ fontSize: '11px' }}>{t("eventRegistrations.historyModal.stillInside")}</Tag>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty description={t("eventRegistrations.historyModal.noCheckinRecords")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </div>
              </div>
            ) : (
              <Empty description={t("eventRegistrations.historyModal.noHistoryData")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Modal>

          {/* Add/Edit Participant Modal — shared by "Add Guest" (regular) and "Add VIP Participant" */}
          <Modal
            title={
              editingParticipant
                ? t("eventRegistrations.addEditModal.editTitle")
                : guestTypeToAdd === "vip"
                  ? t("eventRegistrations.addEditModal.addVipTitle")
                  : t("eventRegistrations.addEditModal.addGuestTitle")
            }
            visible={addVipModalVisible}
            onCancel={() => {
              setAddVipModalVisible(false);
              setEditingParticipant(null);
              setGuestTypeToAdd("vip");
              addVipForm.resetFields();
            }}
            footer={null}
          >
            <Form
              form={addVipForm}
              layout="vertical"
              onFinish={handleCreateOrUpdateParticipant}
            >
              {/* ... existing fields ... */}

              {!editingParticipant && supportsContactPicker && (
                <Form.Item>
                  <Button
                    icon={<ContactsOutlined />}
                    onClick={handlePickFromContacts}
                    block
                  >
                    {t("eventRegistrations.addEditModal.pickFromContacts")}
                  </Button>
                </Form.Item>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  label={t("eventRegistrations.addEditModal.firstName")}
                  name="firstName"
                  rules={[{ required: true, message: t("eventRegistrations.addEditModal.firstNameRequired") }]}
                >
                  <Input placeholder={t("eventRegistrations.addEditModal.firstNamePlaceholder")} />
                </Form.Item>
                <Form.Item
                  label={t("eventRegistrations.addEditModal.lastName")}
                  name="lastName"
                  rules={[{ required: true, message: t("eventRegistrations.addEditModal.lastNameRequired") }]}
                >
                  <Input placeholder={t("eventRegistrations.addEditModal.lastNamePlaceholder")} />
                </Form.Item>
              </div>

              <Form.Item
                label={t("eventRegistrations.addEditModal.email")}
                name="email"
                rules={[
                  ...(isMarriageEvent
                    ? []
                    : [{ required: true, message: t("eventRegistrations.addEditModal.emailRequired") }]),
                  { type: "email", message: t("eventRegistrations.addEditModal.emailInvalid") },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder={t("eventRegistrations.addEditModal.emailPlaceholder")} />
              </Form.Item>

              <Form.Item
                label={t("eventRegistrations.addEditModal.phone")}
                name="phone"
                rules={
                  isMarriageEvent
                    ? [{ required: true, message: t("eventRegistrations.addEditModal.phoneRequired") }]
                    : []
                }
              >
                <PhoneField />
              </Form.Item>

              {!isMarriageEvent && (
                <div className="grid grid-cols-2 gap-4">
                  <Form.Item label={t("eventRegistrations.addEditModal.company")} name="company">
                    <Input prefix={<BankOutlined />} placeholder={t("eventRegistrations.addEditModal.companyPlaceholder")} />
                  </Form.Item>
                  <Form.Item label={t("eventRegistrations.addEditModal.jobTitle")} name="jobTitle">
                    <Input prefix={<IdcardOutlined />} placeholder={t("eventRegistrations.addEditModal.jobTitlePlaceholder")} />
                  </Form.Item>
                </div>
              )}

              {/*
              <Form.Item label="Company Logo (for Wallet Pass)">
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'start'
                }}>
                  <span style={{ marginRight: '8px', fontSize: '16px' }}>ℹ️</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                      Company logos are managed centrally
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>
                      The company logo for wallet passes is automatically fetched from your company settings.
                      To update it, go to Settings → Company Profile → Company Logo.
                    </div>
                  </div>
                </div>
              </Form.Item>
              */}

              <Form.Item label={t("eventRegistrations.addEditModal.city")} name="city">
                <Input placeholder={t("eventRegistrations.addEditModal.cityPlaceholder")} />
              </Form.Item>
              {/* Country field hidden from UI; "country" form value stays unset */}

              {isMarriageEvent && (
                <Form.Item
                  label={t("eventRegistrations.addEditModal.companions")}
                  name="companions"
                  tooltip={t("eventRegistrations.addEditModal.companionsTooltip")}
                  initialValue={0}
                >
                  <InputNumber min={0} precision={0} parser={digitsParser} formatter={digitsFormatter(i18n.language?.startsWith("ar"))} style={{ width: "100%" }} placeholder="0" />
                </Form.Item>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={addVipLoading}
                  block
                >
                  {editingParticipant
                    ? t("eventRegistrations.addEditModal.updateParticipant")
                    : guestTypeToAdd === "vip"
                      ? t("eventRegistrations.addEditModal.addVipParticipant")
                      : t("eventRegistrations.addEditModal.addGuest")}
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </Content >
    </Layout >
  );
};

export default EventRegistrationsPage;
